import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { routineCategoryLabel } from '@euler/app/flows/apdm/imports/functions/routineCategoryLabel';
import { apdmFailureReasonText } from '@euler/functions/apdm/campaignLabels';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import {
  ApdmCampaignRecipientFilterCounts,
  ApdmCampaignRecipientView,
  ApdmOutreachState,
  ApdmReachability,
  ApdmRecipientSearchField,
  ApdmSendState,
} from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorAlert } from '@euler/utils';
import {
  Calendar,
  Clock,
  CloudOff,
  Gauge,
  Info,
  LucideIcon,
  Phone,
  RefreshCw,
  Reply,
  Search,
  Users,
  Warehouse,
  Wrench,
} from 'lucide-react-native';
import { MotiView } from 'moti';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import { Observable } from 'rxjs';
import {
  ApdmCard,
  ApdmEmptyList,
  ApdmFact,
  ApdmFactFooter,
  ApdmListSkeleton,
  ApdmPlate,
  ApdmTag,
} from '../components/ApdmSurface';
import { RecipientSearchBar } from './RecipientSearchBar';
import {
  ApdmCustomerFilter,
  apdmCustomerLook,
  apdmDay,
  apdmShortTime,
  kApdmCustomerFilters,
} from './campaignDetailText';

const kPageSize = 30;

type RowAction = 'arrived' | 'book' | 'retry' | 'contact';

const kActionLabels: Record<RowAction, string> = {
  arrived: '已到店',
  book: '约到店',
  retry: '重试发送',
  contact: '恢复联系',
};

/** The one action a row offers, where it applies. */
function rowAction(row: ApdmCampaignRecipientView): RowAction | null {
  if (!row.canManage) return null;
  if (row.reachability === ApdmReachability.Unreachable) return 'retry';
  if (row.doNotContact) return 'contact';
  if (row.outreachState === ApdmOutreachState.Engaged && row.leadId)
    return 'book';
  if (row.outreachState === ApdmOutreachState.Replied) return 'arrived';
  return null;
}

/** How many customers a filter shows; 0 before the counts arrive. */
function filterCount(
  counts: ApdmCampaignRecipientFilterCounts | null,
  filter: ApdmCustomerFilter,
): number {
  if (!counts) return 0;
  return filter === 'all'
    ? counts.all
    : filter === 'undelivered'
    ? counts.undelivered
    : counts.outreachStates[filter];
}

/**
 * Whether the first message is still to go out in turn. A customer the
 * campaign ended before, or whose send failed, has a reason instead.
 */
function waiting(row: ApdmCampaignRecipientView): boolean {
  return (
    row.outreachState === ApdmOutreachState.Pending &&
    row.reachability !== ApdmReachability.Unreachable &&
    !row.failureReason &&
    (row.sendState === ApdmSendState.Due ||
      row.sendState === ApdmSendState.InFlight)
  );
}

/**
 * Follow-ups that went out. `sendSeq` moves on when the next one is
 * scheduled, so until that one settles it is not counted.
 */
function followUpsSent(row: ApdmCampaignRecipientView): number {
  return row.sendState === ApdmSendState.Settled
    ? row.sendSeq
    : Math.max(0, row.sendSeq - 1);
}

/** What the store knows of the customer, at the foot of the row. */
function customerFacts(row: ApdmCampaignRecipientView): ApdmFact[] {
  const { facts } = row;
  const phone = facts.customerPhone?.trim();
  const items: Array<ApdmFact | null> = [
    phone
      ? {
          key: 'phone',
          icon: Phone,
          text: phone,
          tone: 'link',
          onPress: () =>
            void Linking.openURL(`tel:${phone}`).catch(onErrorAlert),
        }
      : null,
    facts.visitCount > 0
      ? { key: 'visits', icon: Warehouse, text: `到店 ${facts.visitCount} 次` }
      : null,
    facts.lastVisitAt
      ? {
          key: 'visit',
          icon: Calendar,
          text: `最近到店 ${apdmDay(facts.lastVisitAt)}`,
        }
      : null,
    facts.lastServiceMileage != null
      ? {
          key: 'mileage',
          icon: Gauge,
          text: `上次里程 ${facts.lastServiceMileage} 公里`,
        }
      : null,
    facts.nextDueAt
      ? {
          key: 'due',
          icon: Wrench,
          tone: 'strong',
          text:
            `下次保养 ${apdmDay(facts.nextDueAt)}` +
            (facts.nextDueItems.length
              ? `：${facts.nextDueItems.map(routineCategoryLabel).join('、')}`
              : ''),
        }
      : null,
    followUpsSent(row) > 0
      ? {
          key: 'follow-ups',
          icon: Reply,
          text: `已跟进 ${followUpsSent(row)} 次`,
        }
      : null,
  ];
  return items.filter((item): item is ApdmFact => !!item);
}

/** A quiet line with a small icon, e.g. why a customer goes first. */
const NoteLine = memo(
  ({ icon: Icon, text }: { icon: LucideIcon; text: string }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(6),
          marginTop: rpx(6),
        }}
      >
        <Icon size={rpx(14)} color={aiColors.mutedForeground} />
        <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
          {text}
        </Typography.Text>
      </View>
    );
  },
);

const CustomerRow = memo(
  ({
    row,
    busy,
    onOpen,
    onAction,
  }: {
    row: ApdmCampaignRecipientView;
    busy: boolean;
    onOpen: () => void;
    onAction: (action: RowAction) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const look = apdmCustomerLook(row);
    const action = rowAction(row);
    const given = row.facts.customerName?.trim() || null;
    // Some shops write the plate in the name field; it is shown once.
    const name = given === row.licensePlateNo ? null : given;
    const car = row.facts.vehicleBrandModel?.trim() || null;
    const undelivered = row.reachability === ApdmReachability.Unreachable;
    const message = row.lastMessage;
    const facts = useMemo(() => customerFacts(row), [row]);
    return (
      <ApdmCard
        onPress={onOpen}
        accessibilityLabel={row.licensePlateNo}
        style={{ padding: rpx(14) }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: rpx(8),
            marginBottom: rpx(8),
          }}
        >
          <ApdmPlate plate={row.licensePlateNo} />
          <ApdmTag round text={look.label} color={look.color} />
        </View>
        {name || car ? (
          <Typography.Text
            size={rpx(12)}
            numberOfLines={1}
            color={aiColors.mutedForeground}
          >
            {name ? (
              <Typography.Text bold size={rpx(12)} color={aiColors.foreground}>
                {name}
              </Typography.Text>
            ) : null}
            {name && car ? ' · ' : null}
            {car}
          </Typography.Text>
        ) : null}
        {waiting(row) ? (
          row.reachability === ApdmReachability.Known ? (
            <NoteLine icon={Clock} text="已是微信好友，优先发送" />
          ) : (
            <NoteLine icon={Search} text="未加微信，轮到时搜索添加" />
          )
        ) : undelivered ? (
          <StatusLine
            text={`未发出：${apdmFailureReasonText(row.failureReason)}`}
            color={aiColors.destructive}
          />
        ) : message ? (
          <StatusLine
            text={`${message.sender === 'customer' ? '客户' : '我'}：${
              message.text
            }`}
            time={message.sentAt}
          />
        ) : row.failureReason ? (
          <StatusLine
            text={`还没发出去：${apdmFailureReasonText(row.failureReason)}`}
            color={aiColors.mutedForeground}
          />
        ) : row.reachedAt ? (
          <StatusLine text="已发送" time={row.reachedAt} />
        ) : null}
        <ApdmFactFooter facts={facts} />
        {action ? (
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-end',
              marginTop: rpx(12),
            }}
          >
            <ActionButton
              action={action}
              busy={busy}
              onPress={() => onAction(action)}
            />
          </View>
        ) : null}
      </ApdmCard>
    );
  },
);

/** What happened last, on one line, with its time on the right. */
const StatusLine = memo(
  ({
    text,
    time,
    color = tint(aiColors.foreground, 0.8),
  }: {
    text: string;
    time?: string;
    color?: string;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(8),
          marginTop: rpx(6),
        }}
      >
        <Typography.Text
          size={rpx(12)}
          numberOfLines={1}
          color={color}
          style={{ flex: 1 }}
        >
          {text}
        </Typography.Text>
        {time ? (
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            {apdmShortTime(time)}
          </Typography.Text>
        ) : null}
      </View>
    );
  },
);

/** A row's action: a resend is the primary one; busy while it runs. */
const ActionButton = memo(
  ({
    action,
    busy,
    onPress,
  }: {
    action: RowAction;
    busy: boolean;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const primary = action === 'retry';
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: busy, busy }}
        // Still takes the touch while busy, so a tap does not fall through
        // to the card and open the conversation.
        onPress={busy ? undefined : onPress}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(4),
          paddingHorizontal: rpx(10),
          paddingVertical: rpx(4),
          borderRadius: rpx(8),
          borderWidth: busy || primary ? 0 : 1,
          borderColor: aiColors.border,
          backgroundColor: busy
            ? aiColors.muted
            : primary
            ? aiColors.primary
            : aiColors.card,
          opacity: pressed ? 0.7 : 1,
        })}
      >
        {busy ? (
          <MotiView
            from={{ rotate: '0deg' }}
            animate={{ rotate: '360deg' }}
            transition={{
              type: 'timing',
              duration: 900,
              loop: true,
              repeatReverse: false,
            }}
          >
            <RefreshCw size={rpx(12)} color={aiColors.mutedForeground} />
          </MotiView>
        ) : null}
        <Typography.Text
          medium
          size={rpx(11)}
          color={
            busy
              ? aiColors.mutedForeground
              : primary
              ? '#fff'
              : aiColors.foreground
          }
        >
          {busy ? (primary ? '重试中…' : '处理中…') : kActionLabels[action]}
        </Typography.Text>
      </Pressable>
    );
  },
);

/** The campaign's customers, filtered by where each one stands. */
export const CustomersTab = memo(
  ({
    campaignId,
    version,
    initialFilter,
    onOpen,
    onLead,
    onChanged,
    endReached$,
  }: {
    campaignId: string;
    version: number;
    initialFilter: ApdmCustomerFilter;
    onOpen: (row: ApdmCampaignRecipientView) => void;
    onLead: (leadId: string) => void;
    /** A row action changed the campaign's numbers. */
    onChanged: () => void;
    /** The page scrolled near its end: time for the next page. */
    endReached$: Observable<void>;
  }) => {
    const { rpx } = useResponsiveContext();
    const { apdmCampaignService } = useServiceFactory();
    const confirm = useConfirm();
    const [filter, setFilter] = useState(initialFilter);
    const [searchText, setSearchText] = useState('');
    const [searchBy, setSearchBy] = useState(ApdmRecipientSearchField.Plate);
    // What the list is searched by: the text once typing pauses.
    const [search, setSearch] = useState('');
    useEffect(() => {
      const timer = setTimeout(() => setSearch(searchText.trim()), 300);
      return () => clearTimeout(timer);
    }, [searchText]);
    const [rows, setRows] = useState<ApdmCampaignRecipientView[] | null>(null);
    const [next, setNext] = useState<string | null>(null);
    // Where the next page starts, as of the page that landed last. The state
    // above draws the footer; this is what a read starts from, since a
    // scroll event can arrive before the render that would update `next`.
    const cursor = useRef<string | null>(null);
    // Kept across filters: every first page brings them, whatever its filter.
    const [counts, setCounts] =
      useState<ApdmCampaignRecipientFilterCounts | null>(null);
    const [failed, setFailed] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);
    const [reload, setReload] = useState(0);
    // A page that failed stops the automatic loading until tapped again.
    const [moreFailed, setMoreFailed] = useState(false);

    const load = useCallback(
      async (after: string | null) =>
        await apdmCampaignService.recipients({
          campaignId,
          expectedVersion: version,
          afterRecipientId: after,
          limit: kPageSize,
          outreachState:
            filter === 'all' || filter === 'undelivered' ? null : filter,
          reachability:
            filter === 'undelivered' ? ApdmReachability.Unreachable : null,
          search: search || null,
          searchBy,
        }),
      [apdmCampaignService, campaignId, version, filter, search, searchBy],
    );
    useEffect(() => {
      let active = true;
      cursor.current = null;
      setRows(null);
      setFailed(false);
      load(null)
        .then(page => {
          if (!active) return;
          cursor.current = page.nextAfterRecipientId;
          setRows(page.rows);
          setNext(page.nextAfterRecipientId);
          if (page.filterCounts) setCounts(page.filterCounts);
        })
        .catch(error => {
          console.error('APDM campaign customers failed', error);
          if (active) setFailed(true);
        });
      return () => {
        active = false;
      };
    }, [load, reload]);
    // The loader of the list on screen: a page that lands after the filter
    // changed belongs to the old list and is dropped.
    const current = useRef(load);
    current.current = load;
    // Scroll events come faster than renders; one page is read at a time.
    const reading = useRef(false);
    const more = useCallback(async () => {
      const after = cursor.current;
      if (!after || busy || reading.current) return;
      reading.current = true;
      setBusy('more');
      setMoreFailed(false);
      try {
        const page = await load(after);
        if (current.current !== load || cursor.current !== after) return;
        cursor.current = page.nextAfterRecipientId;
        setRows(previous => [...(previous ?? []), ...page.rows]);
        setNext(page.nextAfterRecipientId);
      } catch (error) {
        console.error('APDM campaign customers failed', error);
        setMoreFailed(true);
      } finally {
        reading.current = false;
        setBusy(null);
      }
    }, [busy, load]);
    // The latest reader for the page's end events; after a failed page the
    // list waits for a tap instead.
    const onEnd = useRef(() => {});
    onEnd.current = () => {
      if (!moreFailed) void more();
    };
    useEffect(() => {
      const subscription = endReached$.subscribe(() => onEnd.current());
      return () => subscription.unsubscribe();
    }, [endReached$]);
    const act = async (row: ApdmCampaignRecipientView, action: RowAction) => {
      if (action === 'book') {
        onLead(row.leadId!);
        return;
      }
      const ask = {
        arrived: {
          title: '标记已到店？',
          message: '这位客户会算作这次活动转化的客户。',
        },
        retry: {
          title: '重新发送？',
          message: '会在下一个发送时段再给这位客户发一次。',
        },
        contact: {
          title: '恢复联系？',
          message: '客户说过不想再收到消息，确认已经征得同意再恢复。',
        },
      }[action];
      if (!(await confirm(ask))) return;
      setBusy(row.id);
      const ref = {
        campaignId,
        expectedVersion: version,
        recipientId: row.id,
      };
      try {
        if (action === 'arrived')
          await apdmCampaignService.markArrived({ ...ref, orderNo: null });
        else if (action === 'retry')
          await apdmCampaignService.resumeRecipient({
            ...ref,
            expectedAttempt: row.attempt,
            expectedSendSeq: row.sendSeq,
          });
        else
          await apdmCampaignService.clearContact({
            ...ref,
            expectedContactFingerprint: row.contactFingerprint ?? '',
          });
        setReload(value => value + 1);
        onChanged();
      } catch (error) {
        console.error('APDM customer action failed', error);
        await confirm({
          title: '没有操作成功',
          message: '客户的状态可能刚刚变过，刷新后再试。',
          cancelButtonDisabled: true,
        });
      } finally {
        setBusy(null);
      }
    };

    return (
      <View style={{ gap: rpx(12) }}>
        <RecipientSearchBar
          text={searchText}
          field={searchBy}
          onChangeText={setSearchText}
          onChangeField={setSearchBy}
        />
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: rpx(8) }}
        >
          {kApdmCustomerFilters.map(option => {
            const active = option.value === filter;
            const count = filterCount(counts, option.value);
            return (
              <Pressable
                key={option.value}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                onPress={() => setFilter(option.value)}
                style={{
                  paddingHorizontal: rpx(12),
                  paddingVertical: rpx(6),
                  borderRadius: rpx(16),
                  borderWidth: 1,
                  borderColor: active ? aiColors.primary : aiColors.border,
                  backgroundColor: active ? aiColors.primary : aiColors.card,
                }}
              >
                <Typography.Text
                  medium
                  size={rpx(12)}
                  color={active ? '#fff' : aiColors.mutedForeground}
                >
                  {option.label}
                  {count ? (
                    // Lighter than the label: a regular weight, smaller
                    // and faded.
                    <Typography.Text
                      size={rpx(10)}
                      color={
                        active
                          ? tint('#FFFFFF', 0.75)
                          : tint(aiColors.mutedForeground, 0.7)
                      }
                    >
                      {` · ${count}`}
                    </Typography.Text>
                  ) : null}
                </Typography.Text>
              </Pressable>
            );
          })}
        </ScrollView>
        {failed ? (
          <ApdmEmptyList
            icon={CloudOff}
            text="客户列表没有加载出来"
            onRetry={() => setReload(value => value + 1)}
          />
        ) : !rows ? (
          <ApdmListSkeleton />
        ) : rows.length === 0 ? (
          <ApdmEmptyList
            icon={search ? Search : Users}
            text={search ? '没有找到符合的客户' : '这里还没有客户'}
          />
        ) : (
          <View style={{ gap: rpx(10) }}>
            {rows.some(waiting) ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: rpx(6),
                }}
              >
                <Info size={rpx(13)} color={aiColors.mutedForeground} />
                <Typography.Text
                  size={rpx(11)}
                  color={aiColors.mutedForeground}
                >
                  已加微信的客户先发，其余轮到时再搜索添加
                </Typography.Text>
              </View>
            ) : null}
            {rows.map(row => (
              <CustomerRow
                key={row.id}
                row={row}
                busy={busy === row.id}
                onOpen={() => onOpen(row)}
                onAction={action => void act(row, action)}
              />
            ))}
            {next ? (
              moreFailed ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => void more()}
                  style={{ paddingVertical: rpx(12), alignItems: 'center' }}
                >
                  <Typography.Text size={rpx(13)} color={aiColors.primary}>
                    没有加载出来，点此重试
                  </Typography.Text>
                </Pressable>
              ) : (
                <ActivityIndicator
                  color={aiColors.primary}
                  style={{ paddingVertical: rpx(12) }}
                />
              )
            ) : null}
          </View>
        )}
      </View>
    );
  },
);
